import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Clock, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { BRAND, SERVICES } from "@/data/content";
import { submitContact } from "@/lib/api";
import HammersBackdrop from "@/components/site/HammersBackdrop";

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill out your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success("Thanks! We'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + BRAND.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative bg-paper-alt py-24 md:py-32 overflow-hidden"
      data-testid="contact-form-section"
    >
      <HammersBackdrop density="light" />
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#16a34a]">
              Contact Us
            </div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#1c1917] mt-2 tracking-tight">
              Request a free estimate.
            </h2>
            <p className="mt-5 text-[#3c4043] text-base md:text-lg leading-relaxed">
              Tell us about the project and we&rsquo;ll get back to you within one
              business day. Prefer to talk now? Call us — a real person picks
              up.
            </p>

            <div className="mt-8 space-y-4">
              <a
                href={BRAND.phoneHref}
                data-testid="contact-phone-cta"
                className="flex items-center gap-4 bg-white border border-[#e7e5df] rounded-2xl p-5 hover:border-[#16a34a] hover:shadow-md transition group"
              >
                <span className="h-12 w-12 rounded-2xl bg-[#16a34a] text-white inline-flex items-center justify-center group-hover:rotate-6 transition">
                  <Phone className="w-5 h-5" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#57534e]">
                    Call now
                  </div>
                  <div className="font-display font-extrabold text-2xl text-[#1c1917]">
                    {BRAND.phone}
                  </div>
                </div>
              </a>
              <a
                href={`mailto:${BRAND.email}`}
                className="flex items-center gap-4 bg-white border border-[#e7e5df] rounded-2xl p-5 hover:border-[#16a34a] transition"
                data-testid="contact-email-link"
              >
                <span className="h-12 w-12 rounded-2xl bg-[#1c1917] text-white inline-flex items-center justify-center">
                  <Mail className="w-5 h-5" />
                </span>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#57534e]">
                    Email
                  </div>
                  <div className="font-bold text-[#1c1917] break-all">
                    {BRAND.email}
                  </div>
                  <a
                    href={`mailto:${BRAND.email2}`}
                    className="font-bold text-[#1c1917] break-all hover:text-[#16a34a] transition block mt-0.5"
                    data-testid="contact-email-link-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {BRAND.email2}
                  </a>
                </div>
              </a>
              <div className="flex items-center gap-4 bg-white border border-[#e7e5df] rounded-2xl p-5">
                <span className="h-12 w-12 rounded-2xl bg-[#eab308] text-[#1c1917] inline-flex items-center justify-center">
                  <Clock className="w-5 h-5" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#57534e]">
                    Hours
                  </div>
                  <div className="font-bold text-[#1c1917]">
                    Mon – Fri · 8 AM – 5 PM
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="bg-white rounded-3xl border border-[#e7e5df] p-7 md:p-10 shadow-sm">
              {done ? (
                <div
                  className="flex flex-col items-center justify-center text-center py-12"
                  data-testid="contact-form-success"
                >
                  <div className="h-16 w-16 rounded-full bg-[#16a34a]/10 text-[#16a34a] inline-flex items-center justify-center">
                    <CheckCircle2 className="w-9 h-9" />
                  </div>
                  <h3 className="font-display font-extrabold text-2xl text-[#1c1917] mt-5">
                    Message received.
                  </h3>
                  <p className="text-[#57534e] mt-2 max-w-sm">
                    Thanks for reaching out. We&rsquo;ll be in touch within one
                    business day. For urgent jobs, call {BRAND.phone}.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    className="mt-6 inline-flex items-center px-5 h-11 rounded-full border-2 border-[#1c1917] font-bold text-sm hover:bg-[#1c1917] hover:text-white transition"
                    data-testid="contact-send-another"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="font-bold text-sm text-[#1c1917]">
                        Full name *
                      </Label>
                      <Input
                        id="name"
                        data-testid="contact-input-name"
                        value={form.name}
                        onChange={onChange("name")}
                        placeholder="Your full name"
                        className="mt-2 h-12 rounded-xl"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="font-bold text-sm text-[#1c1917]">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-input-email"
                        value={form.email}
                        onChange={onChange("email")}
                        placeholder="you@example.com"
                        className="mt-2 h-12 rounded-xl"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="phone" className="font-bold text-sm text-[#1c1917]">
                        Phone
                      </Label>
                      <Input
                        id="phone"
                        data-testid="contact-input-phone"
                        value={form.phone}
                        onChange={onChange("phone")}
                        placeholder="(727) 555-0100"
                        className="mt-2 h-12 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label className="font-bold text-sm text-[#1c1917]">
                        Service interested in
                      </Label>
                      <Select
                        value={form.service}
                        onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
                      >
                        <SelectTrigger
                          className="mt-2 h-12 rounded-xl"
                          data-testid="contact-select-service"
                        >
                          <SelectValue placeholder="Choose a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {SERVICES.map((s) => (
                            <SelectItem key={s.slug} value={s.title}>
                              {s.title}
                            </SelectItem>
                          ))}
                          <SelectItem value="Not sure / Other">
                            Not sure / Other
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="font-bold text-sm text-[#1c1917]">
                      Message *
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={onChange("message")}
                      placeholder="Tell us about the project, address, timeline..."
                      className="mt-2 min-h-[140px] rounded-xl"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                    className="w-full inline-flex items-center justify-center gap-2 h-14 rounded-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-bold text-base transition active:scale-[0.99] shadow-[0_10px_30px_-10px_rgba(22,163,74,0.6)]"
                  >
                    {loading ? "Sending..." : (
                      <>
                        Send my request <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-[#57534e] text-center">
                    By submitting you agree to be contacted by ProCraft Solutions
                    about your project.
                  </p>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
